import React, { ReactNode, useState } from 'react';
import { Search, CreditCard, LogOut, Menu, Megaphone, Settings } from "lucide-react";
import { Link, useLocation, useNavigate } from 'react-router-dom'; 
import { PodcastIcon } from '@/components/icons/PodcastIcon';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useUser } from '@/providers/user-provider';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

interface MainLayoutProps {
    children: ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useUser();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    
    // Check if we are on the credits page
    const isCreditsPage = location.pathname.includes('/dashboard/credits');
    const isOutreachPage = location.pathname.includes('/dashboard/outreach');
    const isPodcastsPage = location.pathname.includes('/dashboard/podcasts') || location.pathname === '/dashboard';

    const Logo = () => (
        <div className="flex items-center gap-3">
            <PodcastIcon size={32} color="#E1591B" />
            <span className="text-lg font-semibold text-foreground">Podstack</span>
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-50/50 flex flex-col font-sans">
            {/* Top Navigation Bar */}
            <header className="bg-white border-b border-border sticky top-0 z-50">
                <div className="w-full px-4 md:px-6 h-16 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-8">
                         {/* Mobile Menu Trigger & Content */}
                        <div className="md:hidden">
                            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                                <SheetTrigger asChild>
                                    <Button variant="ghost" size="icon" className="-ml-2">
                                        <Menu className="size-5" />
                                    </Button>
                                </SheetTrigger>
                                <SheetContent side="left" className="p-0 w-72">
                                    <div className="flex flex-col h-full">
                                        <div className="p-4 border-b border-border">
                                            <Logo />
                                        </div>
                                        <nav className="flex-1 p-3 space-y-1">
                                            <Link
                                                to="/dashboard"
                                                onClick={() => setIsMobileMenuOpen(false)}
                                                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                                                    isPodcastsPage
                                                        ? "bg-primary text-white shadow-sm shadow-primary/20"
                                                        : "text-muted-foreground hover:bg-slate-100 hover:text-foreground"
                                                }`}
                                            >
                                                <Search className="size-4" />
                                                <span className="font-medium text-sm">Podcasts</span>
                                            </Link>
                                            <Link
                                                to="/dashboard/outreach"
                                                onClick={() => setIsMobileMenuOpen(false)}
                                                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                                                    isOutreachPage
                                                        ? "bg-primary text-white shadow-sm shadow-primary/20"
                                                        : "text-muted-foreground hover:bg-slate-100 hover:text-foreground"
                                                }`}
                                            >
                                                <Megaphone className="size-4" />
                                                <span className="font-medium text-sm">Outreach</span>
                                            </Link>
                                            <Link
                                                to="/dashboard/credits"
                                                onClick={() => setIsMobileMenuOpen(false)}
                                                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                                                    isCreditsPage
                                                        ? "bg-primary text-white shadow-sm shadow-primary/20"
                                                        : "text-muted-foreground hover:bg-slate-100 hover:text-foreground"
                                                }`}
                                            >
                                                <CreditCard className="size-4" />
                                                <span className="font-medium text-sm">Credit History</span>
                                            </Link>
                                        </nav>
                                        <div className="p-4 border-t border-border">
                                            <Button 
                                                variant="ghost" 
                                                className="w-full justify-start text-red-600 hover:bg-red-50 hover:text-red-600 px-2"
                                                onClick={() => logout()}
                                            >
                                                <LogOut className="size-4 mr-2" />
                                                Log out
                                            </Button>
                                        </div>
                                    </div>
                                </SheetContent>
                            </Sheet>
                        </div>

                        {/* Logo */}
                        <Logo />

                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex items-center gap-1">
                            <Link
                                to="/dashboard"
                                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                    isPodcastsPage
                                        ? "text-primary bg-primary/10"
                                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                                }`}
                            >
                                <Search className="size-4" />
                                Podcasts
                            </Link>
                            <Link
                                to="/dashboard/outreach"
                                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                    isOutreachPage
                                        ? "text-primary bg-primary/10"
                                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                                }`}
                            >
                                <Megaphone className="size-4" />
                                Outreach
                            </Link>
                            <Link
                                to="/dashboard/credits"
                                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                    isCreditsPage
                                        ? "text-primary bg-primary/10"
                                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                                }`}
                            >
                                <CreditCard className="size-4" />
                                Credit History
                            </Link>
                        </nav>
                    </div>

                    {/* Right Side: User Profile */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="relative h-9 rounded-full gap-2 p-1 pr-3 hover:bg-muted">
                                <Avatar className="size-7 border border-slate-200">
                                    <AvatarImage src={user?.avatar || ""} />
                                    <AvatarFallback className="bg-gradient-to-br from-orange-400 to-red-500 text-white text-xs">
                                        {(user?.firstName || user?.name || "U").charAt(0).toUpperCase()}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex flex-col items-start text-left hidden sm:flex">
                                    <span className="text-xs font-medium leading-none">{user?.firstName || user?.name || "User"}</span>
                                    <span className="text-[10px] text-muted-foreground leading-none mt-0.5 max-w-[100px] truncate">{user?.email}</span>
                                </div>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                            {user?.subscriptionTier === 'ENTERPRISE' && (
                                <DropdownMenuItem 
                                    onClick={() => navigate('/dashboard/settings')} 
                                    className="cursor-pointer"
                                >
                                    <Settings className="mr-2 h-4 w-4" />
                                    <span>Settings</span>
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => logout()} className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer">
                                <LogOut className="mr-2 h-4 w-4" />
                                <span>Log out</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            {/* Main Content Area */}
            <main className="flex-1 w-full flex flex-col">
                {children}
            </main>
        </div>
    );
};

export default MainLayout;
